import React, { memo, useMemo } from "react";
import styled from "styled-components/native";
import currency from "currency.js";
import { VDBText, Spacer } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { ProductCategory } from "@utils/constants";
import {
  showSubTitleInGridView,
  getSubTitleText,
} from "@scenes/searchResults/Utils";
import {
  isLinkedPair,
  isLooseParcel,
  isPair,
  isMeleeItem,
} from "@utils/product/productValidators";
import { Diamond } from "@src/types/DiamondDetail";
import { View } from "react-native";
import StrikePrice from "@components/molecules/StrikePrice";

import {
  SearchFilterTypeEnum,
  SearchFilterTypeEnumLowercase,
} from "@components/atoms/Filters/utils";
import Routes from "@navigations/Routes";
import TooltipImage from "@components/molecules/SearchResultListItem/TooltipImage";
import { getAvailabilityStatus } from "@scenes/pdp/utils/utils";

interface PriceSectionProps {
  item: any;
  priceMode: PriceModes;
  displayRetailPrice: boolean;
  userCurrency: string;
  productType: ProductCategory;
  userInfo: any;
  theme: any;
  typography: any;
  customSearchData: any;
  sectionName: string;
  isCustomApp: boolean;
  totalPriceCustomization: any;
  totalPriceLabel: any;
  t: any;
  onTooltipToggle?: (isOpen: boolean) => void;
  specialPriceFeatureEnabled?: boolean;
  referrerScreen?: string;
  hideSubtitleSection: boolean;
  hidePriceSection: boolean;
}

const PriceSection = memo<PriceSectionProps>(
  ({
    item,
    priceMode,
    displayRetailPrice,
    userCurrency,
    productType,
    userInfo,
    theme,
    typography,
    customSearchData,
    sectionName,
    isCustomApp,
    totalPriceCustomization,
    totalPriceLabel,
    t,
    specialPriceFeatureEnabled,
    referrerScreen,
    hideSubtitleSection,
    hidePriceSection,
  }) => {
    const totalPrice = (mItem?: Diamond) => {
      const price = isLooseParcel(mItem)
        ? mItem?.hide_total_sales_price
          ? mItem?.price_per_carat
          : mItem?.total_sales_price
        : mItem?.total_sales_price;
      return price
        ? currency(price, {
            symbol: userCurrency,
            precision: userInfo.isVdbMarketPlace()
              ? 0
              : (userInfo?.roundUpPrices() && !isMeleeItem(mItem)) ||
                productType === ProductCategory.JEWELRY ||
                productType === ProductCategory.LABGROWN_JEWELRY ||
                (isLooseParcel(mItem) && mItem?.hide_total_sales_price)
              ? 0
              : 2,
          }).format()
        : "-";
    };

    const retailPrice = useMemo(() => {
      if (!item?.retail_price) return "-";

      return currency(item.retail_price, {
        symbol: userCurrency,
        precision: userInfo?.roundUpPrices() && !isMeleeItem(item) ? 0 : 2,
      }).format();
    }, [item?.retail_price, userCurrency, userInfo]);

    const specialPrice = (xItem?: Diamond) => {
      if (!xItem?.special_price) return "-";

      return currency(xItem.special_price, {
        symbol: userCurrency,
        precision: userInfo?.roundUpPrices() && !isMeleeItem(xItem) ? 0 : 2,
      }).format();
    };

    const hasSpecialPrice = isCustomApp && item?.special_price != null;

    const shouldShowSubTitle = useMemo(() => {
      return showSubTitleInGridView(
        customSearchData?.[productType],
        sectionName as SearchFilterTypeEnum,
        userInfo?.user
      );
    }, [customSearchData, productType, sectionName, userInfo]);

    const isPairSectionOrItem = useMemo(() => {
      return referrerScreen === Routes.FavoritesListScreen
        ? isPair(item)
        : sectionName === SearchFilterTypeEnum.PAIRS;
    }, [referrerScreen, sectionName, item]);

    const pairsSubTitle =
      customSearchData?.[productType]?.grid_view?.["sub-sections"]?.[
        SearchFilterTypeEnumLowercase.PAIRS
      ]?.price_section?.options?.stock_num;

    const { color } = getAvailabilityStatus(item?.available);

    return (
      <>
        {!hideSubtitleSection &&
        !hidePriceSection &&
        displayRetailPrice &&
        priceMode === PriceModes.ORANGE ? (
          <>
            <VDBText
              color={theme["core-06"]}
              type={typography[theme.isLargeScreen ? "Body-R" : "Minor-R"]}
            >
              {t("searchResult.grid.retailPriceParams.text", {
                price: retailPrice,
              })}
            </VDBText>
            <Spacer size={SpaceType.ExtraSmall} />
          </>
        ) : !hideSubtitleSection &&
          shouldShowSubTitle &&
          (isCustomApp ||
            (!isCustomApp && productType !== ProductCategory.JEWELRY)) &&
          (isPairSectionOrItem ? isCustomApp : true) ? (
          isPairSectionOrItem ? (
            <>
              <TitleContainer
                minHeight={
                  userInfo.twoLineSubTitleInGridViewSearchResult() ||
                  pairsSubTitle?.two_line_sub_title
                    ? 33
                    : 20
                }
              >
                {(() => {
                  if (!pairsSubTitle || !pairsSubTitle?.response_key) {
                    return (
                      <VDBText
                        color={theme["core-06"]}
                        type={
                          typography[theme.isLargeScreen ? "Body-R" : "Minor-R"]
                        }
                        numberOfLines={1}
                      >
                        {item?.stock_num ?? " "}
                      </VDBText>
                    );
                  }
                  const subTitles = (item?.children ?? [])
                    .map(
                      (child: any) =>
                        child?.[pairsSubTitle?.response_key ?? "stock_num"] ??
                        " "
                    )
                    .filter(Boolean);
                  const twoLine =
                    pairsSubTitle?.two_line_sub_title !== undefined
                      ? pairsSubTitle?.two_line_sub_title
                      : userInfo.twoLineSubTitleInGridViewSearchResult();
                  if (isLinkedPair(item)) {
                    return twoLine ? (
                      subTitles.map((subTitle: string) => (
                        <VDBText
                          color={theme["core-06"]}
                          type={
                            typography[
                              theme.isLargeScreen ? "Body-R" : "Minor-R"
                            ]
                          }
                          numberOfLines={1}
                        >
                          {subTitle.trim() ?? "-"}
                        </VDBText>
                      ))
                    ) : (
                      <VDBText
                        color={theme["core-06"]}
                        type={
                          typography[theme.isLargeScreen ? "Body-R" : "Minor-R"]
                        }
                        numberOfLines={1}
                      >
                        {subTitles.join(" | ").trim() ?? " "}
                      </VDBText>
                    );
                  } else {
                    return (
                      <VDBText
                        color={theme["core-06"]}
                        type={
                          typography[theme.isLargeScreen ? "Body-R" : "Minor-R"]
                        }
                        numberOfLines={
                          userInfo.twoLineSubTitleInGridViewSearchResult()
                            ? 2
                            : 1
                        }
                      >
                        {pairsSubTitle?.response_key === "sub_title"
                          ? getSubTitleText(
                              item.sub_title,
                              userInfo.twoLineSubTitleInGridViewSearchResult(),
                              productType
                            )
                          : item[pairsSubTitle?.response_key]
                          ? item[pairsSubTitle?.response_key]
                          : item.stock_num
                          ? item.stock_num
                          : " "}
                      </VDBText>
                    );
                  }
                })()}
              </TitleContainer>
              <Spacer size={SpaceType.ExtraSmall} />
            </>
          ) : (
            <>
              {!hideSubtitleSection && (
                <>
                  <TitleContainer
                    minHeight={
                      userInfo.twoLineSubTitleInGridViewSearchResult() ||
                      (pairsSubTitle?.two_line_sub_title &&
                        (referrerScreen === Routes.FavoritesListScreen ||
                          sectionName === SearchFilterTypeEnum.PAIRS))
                        ? 33
                        : 20
                    }
                  >
                    <VDBText
                      color={theme["core-06"]}
                      type={
                        typography[theme.isLargeScreen ? "Body-R" : "Minor-R"]
                      }
                      numberOfLines={
                        userInfo.twoLineSubTitleInGridViewSearchResult() ? 2 : 1
                      }
                    >
                      {item?.sub_title
                        ? getSubTitleText(
                            item.sub_title,
                            userInfo.twoLineSubTitleInGridViewSearchResult(),
                            productType
                          )
                        : " "}
                    </VDBText>
                  </TitleContainer>
                  <Spacer size={SpaceType.ExtraSmall} />
                </>
              )}
            </>
          )
        ) : (
          <>
            {!hideSubtitleSection && (
              <>
                <TitleContainer
                  minHeight={
                    userInfo.twoLineSubTitleInGridViewSearchResult() ? 33 : 20
                  }
                >
                  <VDBText
                    color={theme["core-06"]}
                    type={
                      typography[theme.isLargeScreen ? "Body-R" : "Minor-R"]
                    }
                    numberOfLines={
                      userInfo.twoLineSubTitleInGridViewSearchResult() ? 2 : 1
                    }
                  >
                    {" "}
                  </VDBText>
                </TitleContainer>
                <Spacer size={SpaceType.ExtraSmall} />
              </>
            )}
          </>
        )}
        {!hideSubtitleSection && item?.grid_view_item_label && (
          <>
            <VDBText
              color={theme["core-06"]}
              type={typography[theme.isLargeScreen ? "Body-R" : "Minor-R"]}
              numberOfLines={
                userInfo.twoLineSubTitleInGridViewSearchResult() ? 2 : 1
              }
            >
              {item?.grid_view_item_label || " "}
            </VDBText>
            <Spacer size={SpaceType.ExtraSmall} />
          </>
        )}

        {!hidePriceSection && specialPriceFeatureEnabled && (
          <View style={{ height: 20 }}>
            {hasSpecialPrice && (
              <>
                <View style={{ flexDirection: "row" }}>
                  <StrikePrice
                    value={specialPrice(item)}
                    textColor={theme["error-01"]}
                    lineColor={theme["error-01"]}
                    typography={
                      typography[theme.isLargeScreen ? "Body-R" : "Minor-R"]
                    }
                    numberOfLines={1}
                  />

                  <Spacer size={SpaceType.ExtraSmall} />
                  <VDBText
                    color={theme["core-06"]}
                    type={
                      typography[theme.isLargeScreen ? "Body-R" : "Minor-R"]
                    }
                    numberOfLines={1}
                  >
                    {t("searchResult.grid.retailPrice.text")}
                  </VDBText>
                </View>
                <Spacer size={SpaceType.ExtraSmall} />
              </>
            )}
          </View>
        )}

        {priceMode !== PriceModes.GREEN &&
          (!item?.grid_view_item_label || !isCustomApp) &&
          !hidePriceSection && (
            <>
              <Spacer size={SpaceType.ExtraSmall} />
              <TotalPriceContainer minHeight={24}>
                {item?.pricing_comment &&
                  userInfo.user.price_mode === PriceModes.WHITE && (
                    <ToolTipContainer>
                      <TooltipImage
                        text={item?.pricing_comment + ""}
                        tooltip={item?.pricing_comment + ""}
                        color={color}
                        textAlignment={"right"}
                        type={typography["Minor-R"]}
                        enableTooltip={true}
                        isImageTooltip={true}
                        isAvailableStatusView={false}
                        productType={productType}
                      />
                      <Spacer size={SpaceType.ExtraSmall} />
                    </ToolTipContainer>
                  )}
                <VDBText
                  color={theme["core-05"]}
                  type={typography[theme.isLargeScreen ? "H1-B" : "Body-M"]}
                >
                  {hasSpecialPrice
                    ? totalPrice(item)
                    : (totalPriceCustomization || !isCustomApp) &&
                      totalPrice(item)}
                </VDBText>
              </TotalPriceContainer>
              {((!hidePriceSection && totalPriceLabel) || !isCustomApp) && (
                <VDBText color={theme["core-06"]} type={typography["Minor-R"]}>
                  {t(
                    hasSpecialPrice
                      ? "searchResult.grid.specialPrice.text"
                      : isLooseParcel(item)
                      ? item?.hide_price_per_carat
                        ? "searchResult.grid.pricePerPrice.text"
                        : "searchResult.grid.pricePerCarat.text"
                      : totalPriceLabel?.label ??
                        "searchResult.grid.totalPrice.text"
                  )}
                </VDBText>
              )}
            </>
          )}
      </>
    );
  },
  (prev, next) => {
    return (
      prev.item?.id === next.item?.id &&
      prev.item?.total_sales_price === next.item?.total_sales_price &&
      prev.item?.retail_price === next.item?.retail_price &&
      prev.item?.pricing_comment === next.item?.pricing_comment &&
      prev.item?.sub_title === next.item?.sub_title &&
      prev.item?.children === next.item?.children &&
      prev.item?.stock_num === next.item?.stock_num &&
      prev.item?.grid_view_item_label === next.item?.grid_view_item_label &&
      prev.priceMode === next.priceMode &&
      prev.displayRetailPrice === next.displayRetailPrice &&
      prev.userCurrency === next.userCurrency &&
      prev.productType === next.productType &&
      prev.isCustomApp === next.isCustomApp
    );
  }
);

PriceSection.displayName = "PriceSection";

const TotalPriceContainer = styled.View<{
  minHeight: number;
}>`
  flex-direction: row;
  width: 100%;
  min-height: ${({ minHeight }: any) => minHeight}px;
  justify-content: flex-start;
  align-items: center;
`;

const TitleContainer = styled.View<{
  minHeight: number;
}>`
  min-height: ${({ minHeight }: any) => minHeight}px;
`;

const ToolTipContainer = styled.View`
  flex-direction: row;
`;

export default PriceSection;
